/**
 * Sample amounts for the drawn screens, formatted for the reader's language.
 *
 * The design canvas is drawn in FCFA (`XAF`), and every layout in the redesign
 * was checked against it: `−FCFA 8,500` is eleven characters. That is still the
 * width this page has to survive, because the app is used in West Africa and a
 * real screenshot will contain exactly that. Nothing here may assume a short
 * amount, and no column may be sized to fit `$42`.
 *
 * What the page *shows* is a different question from what it must survive. A
 * reader in the United States has no feel for whether FCFA 6,400 is a lot, so
 * the sample figures are shown in the currency of the language they are
 * reading: dollars for English, euros for French, German, Italian and Spanish.
 *
 * Grouping and symbol placement come from `Intl`, so French gets `1 152 €` and
 * German `1.152 €` without a table of rules here.
 *
 * These are display samples, not a live account. They are fabricated and
 * internally consistent: the Today arithmetic adds up, and the Money screen's
 * accounts sum to its net worth. If you change one figure, change the others.
 */

/**
 * U+2212 MINUS SIGN. Never a hyphen.
 *
 * From the app's own formatter: at 26px a hyphen sits at cap height against
 * lining figures and reads as a dash between two numbers rather than as a sign
 * belonging to one. The hero on this page is 46px, where it is worse.
 *
 * `Intl` emits a hyphen-minus for negatives in most locales, so `money()`
 * formats the absolute value and puts this sign on itself.
 */
export const MINUS = '−';

interface CurrencyForLocale {
  /** ISO 4217 code handed to `Intl`. */
  code: string;
  /** BCP 47 tag that decides grouping and symbol placement. */
  tag: string;
}

/**
 * English reads in dollars; the four European languages read in euros.
 *
 * This is about what a sample figure *means to a reader*, not about what the
 * app supports. The app offers 165 currencies and converts between none of
 * them: you pick one and every figure is stated in it.
 */
const CURRENCIES: Record<string, CurrencyForLocale> = {
  en: { code: 'USD', tag: 'en-US' },
  fr: { code: 'EUR', tag: 'fr-FR' },
  de: { code: 'EUR', tag: 'de-DE' },
  it: { code: 'EUR', tag: 'it-IT' },
  es: { code: 'EUR', tag: 'es-ES' },
};

const FALLBACK = CURRENCIES['en'];

function currencyFor(locale: string): CurrencyForLocale {
  return CURRENCIES[locale] ?? FALLBACK;
}

/**
 * Formats a whole-unit sample amount for `locale` (an `<html lang>` value).
 *
 * Fractions are dropped on purpose. The app shows them; these frames are about
 * the shape of a screen, and a column of `.00` is noise at this size.
 */
export function money(amount: number, locale: string): string {
  const { code, tag } = currencyFor(locale);

  // The space `Intl` puts before a euro symbol is already non-breaking, so a
  // figure cannot wrap between its number and its symbol. Nothing to fix up.
  const formatted = new Intl.NumberFormat(tag, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(amount));

  return amount < 0 ? `${MINUS}${formatted}` : formatted;
}

/**
 * The ISO code shown on the app's Currency row.
 *
 * The real screen shows whichever of the 165 the user picked, as a code. These
 * frames show the one the sample figures are in, so the row agrees with every
 * amount above it.
 */
export function currencyCode(locale: string): string {
  return currencyFor(locale).code;
}
